import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, InvoiceProjectByStatus, invoiceControlStatusConfig } from 'entities';
import { useRouteParams } from 'routes';
import { Navigate } from 'routes/Navigate';

import { Loader, Column, Row, Tag } from 'components/Blocks';
import { GoBackButton, SortFilterButtons } from 'components/Buttons';
import { ListManipulation } from 'components/ListManipulation';
import { ButtonGroup } from 'components/ButtonGroup';
import { ListCollapseIcon, TableIcon } from 'components/Icons';

import { StatusSelect } from './StatusSelect';
import { InvoiceControlCards } from './CardView';
import { InvoiceControlTable } from './TableView';

import { useMultiState, useUrlStateV2, useUser } from 'hooks';

import { listGet } from 'utils/array';

import { getProjectFilters } from './filters';
import { getSorters } from './sorters';
import { appStorage } from 'utils/storage';

export type ProjectInvoiceFilter = Record<'invoicePerson' | 'client', string[]>;

const asyncStatus = [
  invoiceControlStatusConfig.paid.code,
  invoiceControlStatusConfig.canceled.code,
];

const REFs = {
  asyncLoadLimit: 30,
  normalLimit: 50,
};

interface State {
  paginationToken?: string | null;
  projects: InvoiceProjectByStatus[];
  owner?: string[];
  type?: InvoiceProjectByStatus['type'];
  startDate?: string;
  endDate?: string;
}

function ProjectsByStatusInner({ status }: { status: string }) {
  const user = useUser();
  const { t } = useLang();

  const [{ view }, setUrl] = useUrlStateV2({
    view: {
      defaultValue: (appStorage.session.get('controls_by_status_view') as string) ?? 'cards',
      onValue: (v) => appStorage.session.set('controls_by_status_view', v),
      validate: (v) => ['cards', 'table'].includes(v),
    },
  });

  const state = useMultiState<State>({
    projects: [],
  });

  const statusConfig = Object.values(invoiceControlStatusConfig).find(
    ({ routeId }) => status === routeId,
  );

  const isAsyncLoad = asyncStatus.includes(statusConfig?.code as string);

  const loader = loaders.invoice.invoiceProjectsByStatus.use({
    params: {
      status: statusConfig?.code as string,
      limit: isAsyncLoad ? REFs.asyncLoadLimit : REFs.normalLimit,
      paginationToken: state.values.paginationToken || undefined,
    },

    onDataChange: ({ projects, paginationToken }) =>
      state.dispatch({
        paginationToken: paginationToken ?? null,
        projects: (old) => [
          ...old.projects,
          // dev fix due to load on top - will be fixed once search is up
          ...projects.filter(
            (p) => !listGet(old.projects, p.project.id, ({ project }) => project.id),
          ),
        ],
      }),
  });

  // this will be revised as soon as the new search functionality is done

  const { data: teamData } = loaders.teams.list.use({
    defaultValue: [],
  });

  const manipulationsProps = useMemo(() => {
    return {
      filters: getProjectFilters(statusConfig?.code || '', teamData),
      sorters: getSorters(statusConfig?.code || '', user),
    };
  }, [statusConfig, user, teamData]);

  if (!statusConfig) return <Navigate to="invoiceHome" />;

  const isLoading = loader.errorOrLoading && !state.values.projects.length;

  return (
    <>
      <ListManipulation
        itens={state.values.projects}
        {...manipulationsProps}
        storageKey="invoice-control-filters-@@"
      >
        {({ openFilters, parsedItens, hasFilter, hasSorter, openSorters }) => (
          <Column gap="6">
            <Column gap="2" fullWidth>
              <Row>
                <GoBackButton to="invoiceHome" />

                <h5 className="font-medium text-primary">{t('projects-by-status')}</h5>

                <Tag
                  color="#edf8ff"
                  className="px-3 py-1 font-medium text-primary dark:bg-muted"
                  name={isLoading ? <Loader type="dualRing" size={18} /> : parsedItens.length}
                />

                {!!state.values.projects.length && loader.loading && (
                  <Loader type="dualRing" size={18} />
                )}
              </Row>

              <Row gap="2">
                <StatusSelect status={statusConfig.code} />

                <SortFilterButtons
                  hasFilter={hasFilter}
                  hasSort={hasSorter}
                  onFilterClick={openFilters}
                  onSortClick={openSorters}
                />

                {!isAsyncLoad && (
                  <>
                    <span className="mr-auto" />
                    <ButtonGroup
                      value={view}
                      onChange={(view) => setUrl({ view })}
                      options={[
                        { value: 'cards', label: <ListCollapseIcon /> },
                        { value: 'table', label: <TableIcon /> },
                      ]}
                    />
                  </>
                )}
              </Row>
            </Column>

            {isAsyncLoad || view === 'cards' ? (
              <InvoiceControlCards controls={parsedItens} loading={isLoading} />
            ) : (
              <InvoiceControlTable controls={parsedItens} loading={isLoading} />
            )}

            {!parsedItens.length && !isLoading && (
              <p className="text-center">{t('no-projects-on-this-status')}</p>
            )}
          </Column>
        )}
      </ListManipulation>
    </>
  );
}

function ProjectsByStatus() {
  const { status } = useRouteParams('invoiceProjectsByStatus');

  return <ProjectsByStatusInner key={status} status={status} />;
}

export default ProjectsByStatus;
